import {
    GetNftRewardResponse,
    NftOwnership,
    NftsApi,
    RedeemNftRewardResponse,
    RewardType,
} from "@devour/client";
import {Dispatch, ReactElement, ReactNode, SetStateAction, useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import Toast from "@/components/Toast";
import GoVipRewardsSelectedNftFooter from "@/components/goVip/GoVipRewardsSelectedNftFooter";
import classNames from "classnames";
import { IStore } from "@/redux/defaultStore";
import { addError } from "@/redux/meta/metaActions";
import { useGetRedeemableNftOwnerships } from "@/hooks/useGetRedeemableNftOwnerships";
import { useGetNftOwnershipsForUser } from "@/hooks/useGetNftOwnershipsForUser";
import { GoVipRewardsNftTooltip } from "./GoVipRewardsNftTooltip";
import getConfig from "@/utils/getConfig";
import useRefreshRewardData from "@/hooks/useRefreshRewardData";
import {useGetNftOwnerships} from "@/hooks/useGetNftOwnerships";
import isLootboxUnburnt from "@/utils/isLootboxUnburnt";
import {parseChain} from "@/utils/parseChain";
import { useAsset } from "@/hooks/useAsset";

interface Props {
    reward: GetNftRewardResponse;
    ownership: NftOwnership;
    toggleDrawer: () => void;
    setSelectedNft: Dispatch<SetStateAction<NftOwnership>>;
}

export enum RewardNftRedemptionState {
    NOT_INITIATED,
    OPENING,
    IN_PROGRESS,
    DONE,
}

function GoVipRewardsSelectedNft(props: Props) {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { reward } = props;
    const rewardMetadata = reward.nftReward;

    const [showToast, setShowToast] = useState<boolean>(false);
    const [prizeWon, setPrizeWon] = useState<RedeemNftRewardResponse>(undefined);
    const [redemptionState, setRedemptionState] = useState<RewardNftRedemptionState>(RewardNftRedemptionState.OPENING);
    const [revealPrize, setRevealPrize] = useState<boolean>(false);
    const {data: redeemableNftsResponse, refetch: refetchRedeemableNftOwnerships} = useGetRedeemableNftOwnerships(fullToken);
    const { refetch: refetchNftOwnershipsForUser } = useGetNftOwnershipsForUser(fullToken);
    const {refetch: refetchNftOwnerships} = useGetNftOwnerships(fullToken);
    const {refetch: refreshRewardData} = useRefreshRewardData();
    const sparklingEffectTimeout: number = rewardMetadata.transitionEffectEnabled ? 1500 : 0;
    const rewardImage =
        rewardMetadata.image?.url ?? `${import.meta.env.VITE_CDN_URL}/images/govip-nft-rewards-default-image.gif`;
    const nextLootbox = redeemableNftsResponse?.lootboxes.find((nftOwnership) => {
        return nftOwnership.id !== props.ownership.id && nftOwnership.nftTrackerId === props.ownership.nftTrackerId;
    });
    const {data: winningImageAsset} = useAsset(prizeWon?.nftPrize?.rewardConfig?.assetId);

    useEffect(() => {
        resetProgress();
    }, [props.ownership]);

    function resetProgress(): void {
        setPrizeWon(undefined);
        if (isLootboxUnburnt(props.ownership)) {
            setRedemptionState(RewardNftRedemptionState.NOT_INITIATED);
        } else {
            setRedemptionState(RewardNftRedemptionState.OPENING);
        }
        setRevealPrize(false);
        setShowToast(false);
    }

    async function onConfirmRedeem(): Promise<void> {
        setShowToast(true);
        setRedemptionState(RewardNftRedemptionState.IN_PROGRESS);
        await new NftsApi(getConfig(fullToken)).burnNftReward({
            id: props.ownership?.id,
        });
        void refetchRedeemableNftOwnerships();
        void refetchNftOwnerships();
        void refetchNftOwnershipsForUser();
    }

    function onTransactionSent(transactionHash: `0x${string}`): void {
        void new NftsApi(getConfig(fullToken)).burnNftReward({
            id: props.ownership?.id,
            burnNftRewardBody: {
                transactionHash,
            },
        });
    }

    async function onTransactionConfirmed(transactionHash?: `0x${string}`): Promise<void> {
        try {
            const prizeWon = await new NftsApi(getConfig(fullToken)).redeemNftReward({
                redeemNftRewardBody: {
                    transactionHash: transactionHash,
                    nftOwnershipId: props.ownership?.id,
                },
            });
            setRevealPrize(true);
            setShowToast(false);
            setTimeout(() => {
                setPrizeWon(prizeWon);
                setRedemptionState(RewardNftRedemptionState.DONE);
            }, sparklingEffectTimeout);
            await refreshRewardData(prizeWon.nftPrize.rewardConfig.type);
        } catch (e) {
            resetProgress();
            dispatch(await addError(e));
        } finally {
            void refetchRedeemableNftOwnerships();
            void refetchNftOwnerships();
            void refetchNftOwnershipsForUser();
        }
    }

    async function onVirtualRedemption(): Promise<void> {
        setRedemptionState(RewardNftRedemptionState.IN_PROGRESS);
        await onTransactionConfirmed();
    }

    function handleToastDismissal(): void {
        setShowToast(false);
    }

    function renderPrizeInfo(): ReactElement {
        const today = new Date();
        const month = today.toLocaleString("en-us", { month: "short" });
        const day = today.getDate();
        const year = today.getFullYear();
        const {prizeRevealName: prizeName, rewardConfig: {type, amount, description }} = prizeWon.nftPrize;
        const renderExtraPrizeInfo = (): ReactElement => {
            switch (type) {
                case RewardType.FUEL:
                    return <> {amount} {import.meta.env.VITE_TOKEN_NAME} have been loaded onto your {import.meta.env.VITE_TOKEN_NAME} Account. Enjoy!</>;
                case RewardType.XP:
                    return <> {prizeName} XP have been added to your account. Enjoy!</>;
                case RewardType.NFT:
                    return prizeWon.trackerName ? <> {prizeWon.trackerName} NFT has been added to your collection. Enjoy!</> : null;
                case RewardType.PROMO:
                    return prizeWon.promoCode
                        ? <> Code <strong>{prizeWon.promoCode}</strong> for <strong>{prizeWon.promoName}</strong> has been added to your account. Apply it at checkout and enjoy!</>
                        : null;
                default:
                    return <>Your prize has been redeemed! Check your email for more info.</>;
            }
        };
        const descriptionText: ReactNode = description && type !== RewardType.PROMO ? description
            : <>
                You have successfully redeemed <strong>{rewardMetadata.nftTracker.name}</strong> NFT and claimed <strong>{prizeName}</strong>.
                {renderExtraPrizeInfo()}
            </>;

        return (
            <div className="govip-rewards-drawer_reward_info">
                <div className="govip-rewards-drawer_reward_info_origin">
                    <span>Prize</span>
                </div>
                <div className="govip-rewards-drawer_reward_info_title">{prizeName}</div>
                <div className="govip-rewards-drawer_reward_info_nftId">Redeemed on {month} {day}, {year}</div>
                <div className="govip-rewards-drawer_reward_info_description">{descriptionText}</div>
            </div>
        );
    }

    function getRewardImageUrl(): string {
        return redemptionState === RewardNftRedemptionState.NOT_INITIATED || !rewardMetadata.redemptionAnimation
            ? rewardImage
            : rewardMetadata.redemptionAnimation.url;
    }

    function onNextLootbox() {
        if (!nextLootbox) {
            return;
        }
        props.setSelectedNft(nextLootbox);
    }

    return (
        <>
            <Toast
                message="Please DO NOT refresh! We are getting your prize ready."
                isOpen={showToast}
                showButton={false}
                onDismiss={handleToastDismissal}
                variant="warning"
                remainIndefinitely={true}
                removeMarginAdjustment={true}
            />
            <div className="govip-rewards-drawer_reward">
                <div className="govip-rewards-drawer_reward_top">
                    <div
                        className={classNames("govip-rewards-drawer_reward_top_image-container", "winning-image", {
                            show: prizeWon,
                        })}
                    >
                        {prizeWon &&
                            <img
                                src={winningImageAsset?.url ?? rewardImage}
                                alt="NFT Prize"
                                className="prize-img"
                            />
                        }
                    </div>

                    <div
                        className={classNames("govip-rewards-drawer_reward_top_image-container", "reward-image", {
                            show: !prizeWon,
                        })}
                    >
                        <img src={getRewardImageUrl()} alt="NFT Reward" />

                        <img
                            src={`${import.meta.env.VITE_CDN_URL}/images/govip-rewards-light-effect.gif`}
                            alt="Light Effect"
                            className={classNames("light-effect", {
                                show: revealPrize && !prizeWon && rewardMetadata.transitionEffectEnabled,
                            })}
                        />
                    </div>

                    {!revealPrize &&
                        <div className="govip-rewards-drawer_reward_top_image-container_tooltips">
                            <GoVipRewardsNftTooltip
                                nftTrackerId={props.ownership?.nftTrackerId}
                            />
                        </div>
                    }
                </div>
                {prizeWon
                    ? renderPrizeInfo()
                    : <div className="govip-rewards-drawer_reward_info">
                        <div className="govip-rewards-drawer_reward_info_origin">
                            <span>{rewardMetadata.origin}</span>
                        </div>
                        <div className="govip-rewards-drawer_reward_info_title">{rewardMetadata.title}</div>
                        <div className="govip-rewards-drawer_reward_info_nftId">#{props.ownership.nftId}</div>
                        <div className="govip-rewards-drawer_reward_info_description">{rewardMetadata.description}</div>
                    </div>
                }
            </div>

            <div className="govip-rewards-drawer_footer">
                <GoVipRewardsSelectedNftFooter
                    redemptionState={redemptionState}
                    prize={prizeWon?.nftPrize}
                    toggleDrawer={props.toggleDrawer}
                    onNextLootbox={nextLootbox && onNextLootbox}
                    ownership={props.ownership}
                    contractAddress={rewardMetadata.nftTracker.contractAddress}
                    onVirtualRedemption={onVirtualRedemption}
                    onTransactionStarted={onConfirmRedeem}
                    onTransactionSubmit={onTransactionSent}
                    onTransactionConfirmed={onTransactionConfirmed}
                    relayerUrl={rewardMetadata.relayerUrl}
                    forwarderAddress={rewardMetadata.forwarderAddress}
                    chain={parseChain(rewardMetadata.nftTracker.evmChain)}
                />
            </div>
        </>
    );
}

export default GoVipRewardsSelectedNft;
